import React, { useEffect, useState, useCallback, useMemo } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { fmt, fmtInt } from "@/lib/format";
import { toast } from "sonner";
import {
  Plus, Trash2, Repeat, XCircle, AlertTriangle, Search, Download, Printer,
  Wallet, Receipt, TrendingUp, TrendingDown, BarChart3, Landmark, Eye, X, FileText,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, Legend,
} from "recharts";
import { printReport } from "@/lib/printReport";
import { printExpense } from "@/lib/printExpense";
import { printPL } from "@/lib/printPL";
import { printInvoice } from "@/lib/printInvoice";
import { printPosReceipt } from "@/lib/printPosReceipt";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

// =================== utils ===================
const todayISO = () => new Date().toISOString().slice(0, 10);
const monthStart = (d = new Date()) => new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
const monthEnd = (d = new Date()) => new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
const yearStart = (d = new Date()) => new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
const yearEnd = (d = new Date()) => new Date(d.getFullYear(), 11, 31).toISOString().slice(0, 10);
const prevMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return { start: start.toISOString().slice(0, 10), end: end.toISOString().slice(0, 10) };
};

const inRange = (d, start, end) => (!start || d >= start) && (!end || d <= end);

const formatPaymentLabel = (inv, lang) => {
  if (inv?.payment_splits?.length) {
    const labels = inv.payment_splits.map((s) => `${s.payment_method} ${Number(s.amount).toFixed(2)}`);
    return lang === "ar" ? `مقسّم: ${labels.join(" + ")}` : `Split: ${labels.join(" + ")}`;
  }
  return inv?.payment_method || "";
};

const printInvoiceNow = (inv, format, template, lang) => {
  const tpl = template || {
    language: lang,
    business_name_en: "iSaloon", business_name_ar: "آي سالون",
    show_customer: true, show_phone: true, show_loyalty: true, show_tax: true, show_discount: true,
    primary_color: "#7A1F3D",
    pos: {
      business_name_ar: "آي سالون", business_name_en: "iSaloon",
      header_title_ar: "إيصال", header_title_en: "Receipt",
      footer_text_ar: "شكراً لزيارتكم!", footer_text_en: "Thank you!",
      paper_width_mm: 80, language: lang,
      show_logo: true, show_customer: true, show_phone: true,
      show_tax: true, show_discount: true, show_qr: true, show_loyalty: true,
    },
  };
  if (format === "pos") printPosReceipt(inv, tpl, lang);
  else printInvoice(inv, tpl, lang);
};

// All "Print" actions on this page route through `printReport` (see
// `lib/printReport.js`) so the operator gets a clean, branded A4 sheet of
// just the report data, never a screenshot of the dashboard.

const downloadCsv = (filename, rows) => {
  const csv = rows.map(r => r.map(c => {
    const s = String(c ?? "");
    return s.includes(",") || s.includes("\"") || s.includes("\n") ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
};

// =================== Filter bar ===================
const FilterBar = ({ range, setRange, preset, setPreset, lang, onPrint, onExport, searchValue, setSearchValue, searchPlaceholder }) => {
  const apply = (p) => {
    setPreset(p);
    if (p === "month") setRange({ start: monthStart(), end: monthEnd() });
    else if (p === "year") setRange({ start: yearStart(), end: yearEnd() });
    else if (p === "prev_month") setRange(prevMonthRange());
  };
  const PRES = [
    { v: "month", l_ar: "هذا الشهر", l_en: "This month" },
    { v: "prev_month", l_ar: "الشهر السابق", l_en: "Prev month" },
    { v: "year", l_ar: "هذه السنة", l_en: "This year" },
    { v: "custom", l_ar: "مخصص", l_en: "Custom" },
  ];
  return (
    <div className="card-surface p-3 flex flex-wrap items-end gap-3 no-print">
      <div className="flex flex-wrap gap-1.5">
        {PRES.map(p => (
          <button key={p.v} onClick={() => apply(p.v)} data-testid={`preset-${p.v}`}
            className={`px-3 py-1.5 rounded-md text-xs border ${preset === p.v ? "btn-gold" : "border-[var(--border)] bg-[var(--surface)] text-[var(--text)]"}`}>
            {lang === "ar" ? p.l_ar : p.l_en}
          </button>
        ))}
      </div>
      <div className="flex items-end gap-2">
        <div>
          <label className="text-[10px] text-[var(--text-muted)] block">{lang === "ar" ? "من" : "From"}</label>
          <input type="date" value={range.start} onChange={e => { setRange({ ...range, start: e.target.value }); setPreset("custom"); }}
            className="input-dark text-xs" data-testid="filter-from"/>
        </div>
        <div>
          <label className="text-[10px] text-[var(--text-muted)] block">{lang === "ar" ? "إلى" : "To"}</label>
          <input type="date" value={range.end} onChange={e => { setRange({ ...range, end: e.target.value }); setPreset("custom"); }}
            className="input-dark text-xs" data-testid="filter-to"/>
        </div>
      </div>
      {setSearchValue && (
        <div className="flex-1 min-w-[200px]">
          <label className="text-[10px] text-[var(--text-muted)] block">{lang === "ar" ? "بحث" : "Search"}</label>
          <div className="relative">
            <Search size={13} className="absolute top-1/2 -translate-y-1/2 start-2.5 text-[var(--text-muted)]"/>
            <input value={searchValue} onChange={e => setSearchValue(e.target.value)}
              placeholder={searchPlaceholder}
              className="input-dark ps-8 text-xs w-full" data-testid="filter-search"/>
          </div>
        </div>
      )}
      <div className="flex gap-2 ms-auto">
        <button onClick={onExport} data-testid="export-csv"
          className="px-3 py-1.5 rounded-md text-xs border border-[var(--border)] hover:border-[var(--gold)] bg-[var(--surface)] text-[var(--text)] flex items-center gap-1">
          <Download size={12}/>CSV
        </button>
        <button onClick={onPrint} data-testid="print-btn"
          className="px-3 py-1.5 rounded-md text-xs border border-[var(--border)] hover:border-[var(--gold)] bg-[var(--surface)] text-[var(--text)] flex items-center gap-1">
          <Printer size={12}/>{lang === "ar" ? "طباعة" : "Print"}
        </button>
      </div>
    </div>
  );
};

// =================== Daily aggregation helper ===================
const aggregateDaily = (items, dateKey, amountKey) => {
  const map = new Map();
  for (const it of items) {
    const d = (it[dateKey] || "").slice(0, 10);
    if (!d) continue;
    map.set(d, (map.get(d) || 0) + Number(it[amountKey] || 0));
  }
  return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([date, value]) => ({ date, value }));
};

// =================== Overview Tab ===================
const OverviewTab = ({ summary, cashPosition, monthlyExp, monthlyInv, lang, onPrintPL, plLoading }) => {
  const cogs = monthlyInv.cogs || 0;
  const grossProfit = monthlyInv.total - cogs;
  const netProfit = grossProfit - monthlyExp.total;
  const grossMargin = monthlyInv.total > 0 ? (grossProfit / monthlyInv.total) * 100 : 0;
  const netMargin = monthlyInv.total > 0 ? (netProfit / monthlyInv.total) * 100 : 0;
  return (
    <div className="space-y-5">
      {/* P&L action bar */}
      <div className="flex items-center justify-end no-print">
        <button onClick={onPrintPL} disabled={plLoading} data-testid="print-pl-btn"
          className="px-4 py-2 rounded-md text-sm border border-[var(--border)] hover:border-[var(--gold)] bg-[var(--surface)] text-[var(--text)] flex items-center gap-2 disabled:opacity-50">
          <Printer size={14}/>{plLoading
            ? (lang === "ar" ? "جارٍ التحميل..." : "Loading...")
            : (lang === "ar" ? "طباعة قائمة الأرباح والخسائر (P&L)..." : "Print P&L Statement...")}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-surface p-5">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><TrendingUp size={11}/>{lang === "ar" ? "الإيرادات (الشهر)" : "Revenue (month)"}</div>
          <div className="font-heading text-3xl gold-text mt-2 num-display" data-testid="kpi-revenue">{fmt(monthlyInv.total)}</div>
        </div>
        <div className="card-surface p-5">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><TrendingDown size={11}/>{lang === "ar" ? "ت. البضاعة (COGS)" : "COGS (month)"}</div>
          <div className="font-heading text-3xl mt-2 num-display text-[var(--text)]" data-testid="kpi-cogs">{fmt(cogs)}</div>
          <div className="text-[10px] text-[var(--text-muted)] mt-1">
            {monthlyInv.cogs_internal > 0 ? (
              <span data-testid="cogs-breakdown">
                {lang === "ar"
                  ? `${fmt(monthlyInv.cogs_sales || 0)} مبيعات · ${fmt(monthlyInv.cogs_internal || 0)} صرف داخلي`
                  : `${fmt(monthlyInv.cogs_sales || 0)} sales · ${fmt(monthlyInv.cogs_internal || 0)} internal`}
              </span>
            ) : (
              lang === "ar" ? "تكلفة المنتجات المباعة والمصروفة داخلياً" : "Cost of products sold and internal use"
            )}
          </div>
        </div>
        <div className="card-surface p-5">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><Receipt size={11}/>{lang === "ar" ? "إجمالي الربح" : "Gross Profit"}</div>
          <div className={`font-heading text-3xl mt-2 num-display ${grossProfit >= 0 ? "gold-text" : "text-red-500"}`} data-testid="kpi-gross-profit">{fmt(grossProfit)}</div>
          <div className="text-[10px] text-[var(--text-muted)] mt-1 num-display">{grossMargin.toFixed(1)}% {lang === "ar" ? "هامش" : "margin"}</div>
        </div>
        <div className="card-surface p-5">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><TrendingDown size={11}/>{lang === "ar" ? "المصروفات (الشهر)" : "Expenses (month)"}</div>
          <div className="font-heading text-3xl rose-text mt-2 num-display" data-testid="kpi-expenses">{fmt(monthlyExp.total)}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-2 gap-4">
        <div className="card-surface p-5">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><Receipt size={11}/>{lang === "ar" ? "صافي الربح (الشهر) = الإيرادات − COGS − المصروفات" : "Net Profit (month) = Revenue − COGS − OpEx"}</div>
          <div className={`font-heading text-4xl mt-2 num-display ${netProfit >= 0 ? "gold-text" : "text-red-500"}`} data-testid="kpi-profit">
            {fmt(netProfit)}
          </div>
          <div className="text-[10px] text-[var(--text-muted)] mt-1 num-display">{netMargin.toFixed(1)}% {lang === "ar" ? "هامش صافي" : "net margin"}</div>
        </div>
        <div className="card-surface p-5">
          <div className="text-[10px] uppercase text-[var(--text-muted)] flex items-center gap-1"><Wallet size={11}/>{lang === "ar" ? "الوضع النقدي" : "Cash Position"}</div>
          <div className="font-heading text-3xl mt-2 num-display text-[var(--text)]" data-testid="kpi-cash">{fmt(cashPosition.total)}</div>
          <div className="text-[10px] text-[var(--text-muted)] mt-1 flex gap-3">
            <span><Wallet size={9} className="inline"/> {fmt(cashPosition.by_kind?.cash || 0)}</span>
            <span><Landmark size={9} className="inline"/> {fmt(cashPosition.by_kind?.bank || 0)}</span>
          </div>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="card-surface p-3 text-center">
            <div className="text-[10px] text-[var(--text-muted)]">{lang === "ar" ? "إجمالي الفواتير (هذا الشهر)" : "Invoices count"}</div>
            <div className="font-heading text-xl mt-1 num-display">{fmtInt(monthlyInv.count)}</div>
          </div>
          <div className="card-surface p-3 text-center">
            <div className="text-[10px] text-[var(--text-muted)]">{lang === "ar" ? "متوسط الفاتورة" : "Avg invoice"}</div>
            <div className="font-heading text-xl mt-1 num-display">{fmt(monthlyInv.count ? monthlyInv.total / monthlyInv.count : 0)}</div>
          </div>
          <div className="card-surface p-3 text-center">
            <div className="text-[10px] text-[var(--text-muted)]">{lang === "ar" ? "عدد المصروفات" : "Expenses count"}</div>
            <div className="font-heading text-xl mt-1 num-display">{fmtInt(monthlyExp.count)}</div>
          </div>
        </div>
      )}
    </div>
  );
};

// =================== Expenses Tab ===================
const ExpensesTab = ({ expenses, accounts, lang, onAdd, onDelete, template }) => {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState({ start: monthStart(), end: monthEnd() });
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return expenses.filter(e => {
      const d = (e.date || "").slice(0, 10);
      if (!inRange(d, range.start, range.end)) return false;
      if (q) {
        const blob = [e.description, e.category, e.expense_number, String(e.amount)].filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [expenses, range, search]);

  const visible = showAll ? filtered : filtered.slice(0, 5);
  const totalFiltered = filtered.reduce((s, e) => s + Number(e.amount || 0), 0);

  // Compare current month vs prev month for the chart
  const chartData = useMemo(() => {
    const curStart = monthStart();
    const curEnd = monthEnd();
    const { start: prevS, end: prevE } = prevMonthRange();
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const cur = new Array(daysInMonth).fill(0);
    const prev = new Array(daysInMonth).fill(0);
    for (const e of expenses) {
      const d = (e.date || "").slice(0, 10);
      const day = parseInt(d.slice(8, 10)) - 1;
      if (d >= curStart && d <= curEnd) cur[day] += Number(e.amount || 0);
      else if (d >= prevS && d <= prevE) prev[day] += Number(e.amount || 0);
    }
    return cur.map((v, i) => ({ day: i + 1, current: +v.toFixed(2), previous: +(prev[i] || 0).toFixed(2) }));
  }, [expenses]);

  const exportCsv = () => {
    const rows = [["Date", "Document", "Category", "Description", "Amount", "Account"]];
    for (const e of filtered) {
      const acc = accounts.find(a => a.account_id === e.account_id);
      rows.push([e.date, e.expense_number || e.expense_id, e.category, e.description, e.amount, acc?.name || ""]);
    }
    rows.push([]);
    rows.push(["TOTAL", "", "", "", totalFiltered, ""]);
    downloadCsv(`expenses_${range.start}_${range.end}.csv`, rows);
    toast.success(lang === "ar" ? "تم التصدير" : "Exported");
  };

  const printPro = () => {
    printReport({
      title: lang === "ar" ? "تقرير المصروفات" : "Expenses Report",
      lang,
      template,
      meta: [
        { label: lang === "ar" ? "الفترة" : "Period", value: `${range.start} → ${range.end}` },
        { label: lang === "ar" ? "البحث" : "Search", value: search || (lang === "ar" ? "—" : "—") },
        { label: lang === "ar" ? "عدد القيود" : "Rows", value: String(filtered.length) },
      ],
      columns: [
        lang === "ar" ? "التاريخ" : "Date",
        lang === "ar" ? "رقم" : "Doc",
        lang === "ar" ? "الفئة" : "Category",
        lang === "ar" ? "الوصف" : "Description",
        lang === "ar" ? "المبلغ" : "Amount",
      ],
      rows: filtered.map(e => [
        { value: (e.date || "").slice(0, 10), className: "muted num" },
        { value: e.expense_number || e.expense_id.slice(-8), className: "muted num" },
        e.category || "—",
        e.description || "—",
        { value: fmt(e.amount), className: "num neg" },
      ]),
      totals: [
        { label: lang === "ar" ? "إجمالي المصروفات" : "Total expenses", value: fmt(totalFiltered), className: "rose" },
      ],
    });
  };

  return (
    <div className="space-y-4" data-testid="expenses-tab">
      <FilterBar lang={lang} range={range} setRange={setRange} preset={preset} setPreset={setPreset}
        searchValue={search} setSearchValue={setSearch}
        searchPlaceholder={lang === "ar" ? "رقم/مبلغ/وصف..." : "Doc / amount / description..."}
        onPrint={printPro} onExport={exportCsv}/>

      <div className="flex items-center justify-between flex-wrap gap-2 no-print">
        <button onClick={onAdd} data-testid="add-expense-btn" className="btn-gold px-4 py-2 rounded-md text-sm flex items-center gap-2">
          <Plus size={14}/>{lang === "ar" ? "إضافة مصروف" : "Add Expense"}
        </button>
        <div className="text-sm text-[var(--text-muted)]">
          {lang === "ar" ? "إجمالي ضمن الفلتر" : "Filtered total"}: <span className="rose-text font-bold num-display">{fmt(totalFiltered)}</span> · {filtered.length} {lang === "ar" ? "قيد" : "rows"}
        </div>
      </div>

      <div className="card-surface p-4">
        <div className="overflow-auto">
          <table className="table-dark">
            <thead><tr>
              <th>{lang === "ar" ? "التاريخ" : "Date"}</th>
              <th>{lang === "ar" ? "رقم" : "Doc"}</th>
              <th>{lang === "ar" ? "الوصف" : "Description"}</th>
              <th>{lang === "ar" ? "الفئة" : "Category"}</th>
              <th>{lang === "ar" ? "المبلغ" : "Amount"}</th>
              <th className="no-print"></th>
            </tr></thead>
            <tbody>
              {visible.map(e => (
                <tr key={e.expense_id}>
                  <td className="num-display text-[var(--text-muted)]">{(e.date || "").slice(0, 10)}</td>
                  <td className="text-[var(--text-muted)] text-xs">{e.expense_number || e.expense_id.slice(-8)}</td>
                  <td className="text-[var(--text)]">
                    {e.description}
                    {e.recurring && <span className="badge badge-gold ms-2 inline-flex items-center gap-1"><Repeat size={9}/>{lang === "ar" ? "متكرر" : "Recurring"}</span>}
                  </td>
                  <td className="text-[var(--text-muted)]">{e.category}</td>
                  <td className="rose-text font-medium num-display">{fmt(e.amount)}</td>
                  <td className="no-print text-end">
                    <div className="inline-flex gap-1">
                      <button
                        onClick={() => printExpense(e, accounts.find(a => a.account_id === e.account_id), template, lang)}
                        data-testid={`print-expense-${e.expense_id}`}
                        title={lang === "ar" ? "طباعة سند الصرف" : "Print voucher"}
                        className="p-1 border border-[var(--border)] rounded hover:border-[var(--gold)] hover:text-[var(--gold)]">
                        <Printer size={12}/>
                      </button>
                      <button onClick={() => onDelete(e.expense_id)} className="p-1 border border-[var(--border)] rounded hover:border-red-500"><Trash2 size={12}/></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center text-[var(--text-muted)] py-6">{lang === "ar" ? "لا توجد مصروفات" : "No expenses"}</td></tr>}
            </tbody>
          </table>
        </div>
        {filtered.length > 5 && (
          <button onClick={() => setShowAll(v => !v)} data-testid="toggle-all-expenses"
            className="mt-3 text-sm gold-text hover:underline no-print">
            {showAll ? (lang === "ar" ? "إخفاء" : "Show less") : (lang === "ar" ? `عرض الكل (${filtered.length})` : `Show all (${filtered.length})`)}
          </button>
        )}
      </div>

      <div className="card-surface p-4">
        <h3 className="font-heading text-lg text-[var(--text)] mb-3 flex items-center gap-2">
          <BarChart3 size={16}/>{lang === "ar" ? "اتجاه المصروفات (الشهر الحالي مقارنة بالسابق)" : "Expenses trend (current vs prev month)"}
        </h3>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)"/>
            <XAxis dataKey="day" tick={{ fontSize: 10 }}/>
            <YAxis tick={{ fontSize: 10 }}/>
            <Tooltip/>
            <Legend wrapperStyle={{ fontSize: 11 }}/>
            <Line type="monotone" dataKey="current" name={lang === "ar" ? "الحالي" : "Current"} stroke="#7A1F3D" strokeWidth={2} dot={false}/>
            <Line type="monotone" dataKey="previous" name={lang === "ar" ? "السابق" : "Previous"} stroke="#999" strokeWidth={2} strokeDasharray="4 4" dot={false}/>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// =================== Invoices Tab ===================
const InvoicesTab = ({ invoices, lang, canVoid, onVoid, onView, template }) => {
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState({ start: monthStart(), end: monthEnd() });
  const [search, setSearch] = useState("");
  const [showAll, setShowAll] = useState(false);

  const dateOf = (i) => (i.created_at || "").slice(0, 10);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return invoices.filter(i => {
      const d = dateOf(i);
      if (!inRange(d, range.start, range.end)) return false;
      if (q) {
        const blob = [i.invoice_number, i.customer_name, String(i.total)].filter(Boolean).join(" ").toLowerCase();
        if (!blob.includes(q)) return false;
      }
      return true;
    });
  }, [invoices, range, search]);

  const visible = showAll ? filtered : filtered.slice(0, 10);
  const paidFiltered = filtered.filter(i => i.status !== "cancelled");
  const totalFiltered = paidFiltered.reduce((s, i) => s + Number(i.total || 0), 0);
  const cancelledCount = filtered.filter(i => i.status === "cancelled").length;

  const chartData = useMemo(() => {
    const curStart = monthStart();
    const curEnd = monthEnd();
    const { start: prevS, end: prevE } = prevMonthRange();
    const daysInMonth = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate();
    const cur = new Array(daysInMonth).fill(0);
    const prev = new Array(daysInMonth).fill(0);
    for (const i of invoices) {
      if (i.status === "cancelled") continue;
      const d = dateOf(i);
      const day = parseInt(d.slice(8, 10)) - 1;
      if (d >= curStart && d <= curEnd) cur[day] += Number(i.total || 0);
      else if (d >= prevS && d <= prevE) prev[day] += Number(i.total || 0);
    }
    return cur.map((v, idx) => ({ day: idx + 1, current: +v.toFixed(2), previous: +(prev[idx] || 0).toFixed(2) }));
  }, [invoices]);

  const exportCsv = () => {
    const rows = [["Date", "Invoice #", "Customer", "Phone", "Total", "Status"]];
    for (const i of filtered) {
      rows.push([dateOf(i), i.invoice_number, i.customer_name, i.customer_phone, i.total, i.status || "paid"]);
    }
    rows.push([]);
    rows.push(["PAID TOTAL", "", "", "", totalFiltered, ""]);
    if (cancelledCount > 0) rows.push(["VOIDED COUNT", "", "", "", cancelledCount, ""]);
    downloadCsv(`invoices_${range.start}_${range.end}.csv`, rows);
    toast.success(lang === "ar" ? "تم التصدير" : "Exported");
  };

  const printPro = () => {
    const paid = filtered.filter(i => i.status !== "cancelled");
    printReport({
      title: lang === "ar" ? "تقرير الفواتير" : "Invoices Report",
      lang,
      template,
      meta: [
        { label: lang === "ar" ? "الفترة" : "Period", value: `${range.start} → ${range.end}` },
        { label: lang === "ar" ? "البحث" : "Search", value: search || "—" },
        { label: lang === "ar" ? "عدد الفواتير" : "Invoices", value: String(filtered.length) },
        { label: lang === "ar" ? "الملغاة" : "Voided", value: String(cancelledCount) },
      ],
      columns: [
        lang === "ar" ? "التاريخ" : "Date",
        lang === "ar" ? "رقم الفاتورة" : "Invoice #",
        lang === "ar" ? "العميل" : "Customer",
        lang === "ar" ? "الإجمالي" : "Total",
        lang === "ar" ? "الحالة" : "Status",
      ],
      rows: filtered.map(i => {
        const cancelled = i.status === "cancelled";
        return [
          { value: dateOf(i), className: "muted num" },
          { value: i.invoice_number, className: "num" },
          i.customer_name || "—",
          { value: fmt(i.total), className: cancelled ? "num muted" : "num pos" },
          {
            value: cancelled
              ? `<span class="badge badge-bad">${lang === "ar" ? "ملغاة" : "VOID"}</span>`
              : `<span class="badge badge-ok">${lang === "ar" ? "مدفوعة" : "Paid"}</span>`,
            html: true,
          },
        ];
      }),
      totals: [
        { label: lang === "ar" ? "إجمالي الفواتير المدفوعة" : "Paid total", value: fmt(totalFiltered), className: "gold" },
        ...(cancelledCount > 0 ? [{ label: lang === "ar" ? "ملغاة" : "Voided", value: String(cancelledCount), className: "rose" }] : []),
      ],
    });
  };

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      <FilterBar lang={lang} range={range} setRange={setRange} preset={preset} setPreset={setPreset}
        searchValue={search} setSearchValue={setSearch}
        searchPlaceholder={lang === "ar" ? "رقم/عميل/مبلغ..." : "Number / customer / amount..."}
        onPrint={printPro} onExport={exportCsv}/>

      <div className="flex items-center justify-end gap-4 text-sm text-[var(--text-muted)] no-print">
        <span>{filtered.length} {lang === "ar" ? "فاتورة" : "invoices"}</span>
        {cancelledCount > 0 && <span className="rose-text">{cancelledCount} {lang === "ar" ? "ملغاة" : "voided"}</span>}
        <span>{lang === "ar" ? "إجمالي المدفوع" : "Paid total"}: <span className="gold-text font-bold num-display">{fmt(totalFiltered)}</span></span>
      </div>

      <div className="card-surface p-4">
        <div className="overflow-auto">
          <table className="table-dark">
            <thead><tr>
              <th>{lang === "ar" ? "التاريخ" : "Date"}</th>
              <th>{lang === "ar" ? "رقم الفاتورة" : "Invoice #"}</th>
              <th>{lang === "ar" ? "العميل" : "Customer"}</th>
              <th>{lang === "ar" ? "الإجمالي" : "Total"}</th>
              <th>{lang === "ar" ? "الحالة" : "Status"}</th>
              <th className="no-print"></th>
            </tr></thead>
            <tbody>
              {visible.map(i => {
                const cancelled = i.status === "cancelled";
                return (
                  <tr key={i.invoice_id} className={cancelled ? "opacity-60" : ""}>
                    <td className="num-display text-[var(--text-muted)]">{dateOf(i)}</td>
                    <td className="text-[var(--text)] font-medium">{i.invoice_number}</td>
                    <td className="text-[var(--text)]">{i.customer_name}</td>
                    <td className={`font-medium num-display ${cancelled ? "line-through text-[var(--text-muted)]" : "gold-text"}`}>{fmt(i.total)}</td>
                    <td>{cancelled
                      ? <span className="badge badge-red">{lang === "ar" ? "ملغاة" : "VOID"}</span>
                      : <span className="badge badge-green">{lang === "ar" ? "مدفوعة" : "Paid"}</span>}</td>
                    <td className="no-print text-end">
                      <div className="inline-flex gap-1">
                        <button onClick={() => onView(i)} data-testid={`view-invoice-${i.invoice_id}`}
                          className="p-1.5 rounded border border-[var(--border)] text-[var(--text-muted)] hover:border-[var(--gold)] hover:text-[var(--gold)]"
                          title={lang === "ar" ? "عرض" : "View"}>
                          <Eye size={12}/>
                        </button>
                        {canVoid && !cancelled && (
                          <button onClick={() => onVoid(i)} data-testid={`void-invoice-${i.invoice_id}`}
                            className="p-1.5 rounded border border-[var(--border)] text-[var(--text-muted)] hover:border-rose-400 hover:text-rose-400"
                            title={lang === "ar" ? "إلغاء الفاتورة" : "Void invoice"}>
                            <XCircle size={12}/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && <tr><td colSpan={6} className="text-center text-[var(--text-muted)] py-6">{lang === "ar" ? "لا توجد فواتير" : "No invoices"}</td></tr>}
            </tbody>
          </table>
        </div>
        {filtered.length > 10 && (
          <button onClick={() => setShowAll(v => !v)} data-testid="toggle-all-invoices"
            className="mt-3 text-sm gold-text hover:underline no-print">
            {showAll ? (lang === "ar" ? "إخفاء" : "Show less") : (lang === "ar" ? `عرض الكل (${filtered.length})` : `Show all (${filtered.length})`)}
          </button>
        )}
      </div>

      <div className="card-surface p-4">
        <h3 className="font-heading text-lg text-[var(--text)] mb-3 flex items-center gap-2">
          <BarChart3 size={16}/>{lang === "ar" ? "اتجاه الإيرادات (الشهر الحالي مقارنة بالسابق)" : "Revenue trend (current vs prev month)"}
        </h3>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)"/>
            <XAxis dataKey="day" tick={{ fontSize: 10 }}/>
            <YAxis tick={{ fontSize: 10 }}/>
            <Tooltip/>
            <Legend wrapperStyle={{ fontSize: 11 }}/>
            <Line type="monotone" dataKey="current" name={lang === "ar" ? "الحالي" : "Current"} stroke="#7A1F3D" strokeWidth={2} dot={false}/>
            <Line type="monotone" dataKey="previous" name={lang === "ar" ? "السابق" : "Previous"} stroke="#999" strokeWidth={2} strokeDasharray="4 4" dot={false}/>
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// =================== Analytics Tab ===================
const AnalyticsTab = ({ expenses, invoices, lang }) => {
  const top = (arr, dateKey, amountKey, n = 5) => {
    const byDay = aggregateDaily(arr, dateKey, amountKey);
    return [...byDay].sort((a, b) => b.value - a.value).slice(0, n);
  };
  const topRevDays = useMemo(() => top(invoices.filter(i => i.status !== "cancelled"), "created_at", "total"), [invoices]);
  const topExpDays = useMemo(() => top(expenses, "date", "amount"), [expenses]);

  const netByDay = useMemo(() => {
    const rev = new Map();
    for (const i of invoices.filter(x => x.status !== "cancelled")) {
      const d = (i.created_at || "").slice(0, 10);
      rev.set(d, (rev.get(d) || 0) + Number(i.total || 0));
    }
    const exp = new Map();
    for (const e of expenses) {
      const d = (e.date || "").slice(0, 10);
      exp.set(d, (exp.get(d) || 0) + Number(e.amount || 0));
    }
    const all = new Set([...rev.keys(), ...exp.keys()]);
    return [...all].filter(Boolean).sort().slice(-14).map(d => ({
      date: d,
      revenue: +(rev.get(d) || 0).toFixed(2),
      expenses: +(exp.get(d) || 0).toFixed(2),
      net: +((rev.get(d) || 0) - (exp.get(d) || 0)).toFixed(2),
    }));
  }, [expenses, invoices]);

  return (
    <div className="space-y-5" data-testid="analytics-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="card-surface p-4">
          <h3 className="font-heading text-base text-[var(--text)] mb-3 flex items-center gap-2"><TrendingUp size={14} className="gold-text"/>{lang === "ar" ? "أعلى 5 أيام إيرادات" : "Top 5 revenue days"}</h3>
          <div className="space-y-1.5">
            {topRevDays.map(d => (
              <div key={d.date} className="flex justify-between text-sm border-b border-[var(--border-subtle)] py-1">
                <span className="text-[var(--text-muted)] num-display">{d.date}</span>
                <span className="gold-text font-medium num-display">{fmt(d.value)}</span>
              </div>
            ))}
            {topRevDays.length === 0 && <div className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "لا توجد بيانات" : "No data"}</div>}
          </div>
        </div>
        <div className="card-surface p-4">
          <h3 className="font-heading text-base text-[var(--text)] mb-3 flex items-center gap-2"><TrendingDown size={14} className="rose-text"/>{lang === "ar" ? "أعلى 5 أيام مصروفات" : "Top 5 expense days"}</h3>
          <div className="space-y-1.5">
            {topExpDays.map(d => (
              <div key={d.date} className="flex justify-between text-sm border-b border-[var(--border-subtle)] py-1">
                <span className="text-[var(--text-muted)] num-display">{d.date}</span>
                <span className="rose-text font-medium num-display">{fmt(d.value)}</span>
              </div>
            ))}
            {topExpDays.length === 0 && <div className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "لا توجد بيانات" : "No data"}</div>}
          </div>
        </div>
      </div>

      <div className="card-surface p-4">
        <h3 className="font-heading text-base text-[var(--text)] mb-3 flex items-center gap-2"><Wallet size={14}/>{lang === "ar" ? "صافي النقد اليومي (آخر 14 يوم)" : "Daily net cash (last 14 days)"}</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={netByDay}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)"/>
            <XAxis dataKey="date" tick={{ fontSize: 9 }}/>
            <YAxis tick={{ fontSize: 10 }}/>
            <Tooltip/>
            <Legend wrapperStyle={{ fontSize: 11 }}/>
            <Bar dataKey="revenue" name={lang === "ar" ? "إيرادات" : "Revenue"} fill="#7A1F3D"/>
            <Bar dataKey="expenses" name={lang === "ar" ? "مصروفات" : "Expenses"} fill="#C97C8C"/>
            <Bar dataKey="net" name={lang === "ar" ? "الصافي" : "Net"} fill="#D4A23C"/>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

// =================== MAIN ===================
const emptyForm = () => ({
  description: "", amount: 0, category: "general", date: todayISO(),
  expense_type: "variable", account_id: "",
  recurring: false, frequency: "monthly", start_date: todayISO(), end_date: "",
});

export default function Accounting() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const { canVoidInvoice } = useAuth();
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [cashPosition, setCashPosition] = useState({ total: 0, by_kind: {} });
  const [expenses, setExpenses] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [internalIssues, setInternalIssues] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [voidFor, setVoidFor] = useState(null);
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [viewInvoice, setViewInvoice] = useState(null);
  const [invoiceTemplate, setInvoiceTemplate] = useState(null);
  const [plLoading, setPlLoading] = useState(false);
  // P&L period picker modal
  const [plPickerOpen, setPlPickerOpen] = useState(false);
  const [plPreset, setPlPreset] = useState("month");
  const [plRange, setPlRange] = useState({ start: monthStart(), end: monthEnd() });
  const [plMonth, setPlMonth] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM

  // Load print template once for invoice headers/branding.
  useEffect(() => {
    api.get("/invoice-template")
      .then((r) => setInvoiceTemplate(r.data || null))
      .catch(() => setInvoiceTemplate(null));
  }, []);

  // Apply a preset to update plRange. Custom & specific-month presets are
  // handled inline by the modal inputs themselves.
  const applyPlPreset = (p) => {
    setPlPreset(p);
    const now = new Date();
    if (p === "month") setPlRange({ start: monthStart(), end: monthEnd() });
    else if (p === "prev_month") setPlRange(prevMonthRange());
    else if (p === "year") setPlRange({ start: yearStart(), end: yearEnd() });
    else if (p === "prev_year") {
      const prev = new Date(now.getFullYear() - 1, 0, 1);
      setPlRange({ start: yearStart(prev), end: yearEnd(prev) });
    } else if (p === "ytd") {
      setPlRange({ start: yearStart(), end: todayISO() });
    } else if (p === "specific_month") {
      const [y, m] = plMonth.split("-").map(Number);
      const ref = new Date(y, m - 1, 1);
      setPlRange({ start: monthStart(ref), end: monthEnd(ref) });
    }
  };

  // When user changes the YYYY-MM picker, refresh the range.
  useEffect(() => {
    if (plPreset !== "specific_month") return;
    const [y, m] = plMonth.split("-").map(Number);
    if (!y || !m) return;
    const ref = new Date(y, m - 1, 1);
    setPlRange({ start: monthStart(ref), end: monthEnd(ref) });
  }, [plMonth, plPreset]);

  const periodLabel = () => {
    if (plPreset === "month") return lang === "ar" ? "الشهر الحالي" : "Current month";
    if (plPreset === "prev_month") return lang === "ar" ? "الشهر السابق" : "Previous month";
    if (plPreset === "year") return lang === "ar" ? "السنة الحالية" : "Current year";
    if (plPreset === "prev_year") return lang === "ar" ? "السنة السابقة" : "Previous year";
    if (plPreset === "ytd") return lang === "ar" ? "من بداية السنة حتى اليوم" : "Year to date";
    if (plPreset === "specific_month") {
      try {
        const d = new Date(plMonth + "-01");
        return d.toLocaleDateString(lang === "ar" ? "ar-EG" : "en-US", { month: "long", year: "numeric" });
      } catch { return plMonth; }
    }
    return lang === "ar" ? "فترة مخصصة" : "Custom period";
  };

  // Print the P&L using the authoritative server-side report.
  const handlePrintPL = async () => {
    const { start, end } = plRange;
    if (!start || !end || start > end) {
      toast.error(lang === "ar" ? "نطاق التاريخ غير صالح" : "Invalid date range");
      return;
    }
    setPlLoading(true);
    try {
      const r = await api.get(`/reports/profit-loss?start=${start}&end=${end}`);
      // Build per-category expenses breakdown from the loaded expenses list.
      const byCat = new Map();
      for (const e of expenses) {
        const d = (e.date || "").slice(0, 10);
        if (d < start || d > end) continue;
        const k = e.category || (lang === "ar" ? "عام" : "general");
        byCat.set(k, (byCat.get(k) || 0) + Number(e.amount || 0));
      }
      const breakdown = [...byCat.entries()]
        .map(([category, total]) => ({ category, total }))
        .sort((a, b) => b.total - a.total);
      printPL({
        pl: r.data,
        period: { start, end, label: periodLabel() },
        expensesBreakdown: breakdown,
        template: invoiceTemplate,
        lang,
      });
      setPlPickerOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || (lang === "ar" ? "تعذر تحميل قائمة الأرباح والخسائر" : "P&L load failed"));
    } finally {
      setPlLoading(false);
    }
  };

  const load = useCallback(async () => {
    const [s, e, i, a, cp, ii] = await Promise.all([
      api.get("/accounting/summary"),
      api.get("/expenses"),
      api.get("/invoices"),
      api.get("/accounts"),
      api.get("/accounts/summary").catch(() => ({ data: { total: 0, by_kind: {} } })),
      api.get("/inventory/internal-issues").catch(() => ({ data: [] })),
    ]);
    setSummary(s.data); setExpenses(e.data); setInvoices(i.data); setAccounts(a.data); setCashPosition(cp.data);
    setInternalIssues(ii.data || []);
  }, []);
  useEffect(() => { load(); }, [load]);

  // Monthly aggregates used by Overview KPIs
  const monthlyExp = useMemo(() => {
    const s = monthStart(), eD = monthEnd();
    const items = expenses.filter(x => inRange((x.date || "").slice(0, 10), s, eD));
    return { total: items.reduce((sum, x) => sum + Number(x.amount || 0), 0), count: items.length };
  }, [expenses]);
  const monthlyInv = useMemo(() => {
    const s = monthStart(), eD = monthEnd();
    const items = invoices.filter(x => x.status !== "cancelled" && inRange((x.created_at || "").slice(0, 10), s, eD));
    // Internal issuance (صرف داخلي) consumes inventory at cost — same accounting
    // bucket as products sold through POS. Both flow through COGS so the
    // Accounting overview reflects EVERY unit of inventory consumed this month.
    const internalCogs = internalIssues
      .filter(x => inRange((x.created_at || "").slice(0, 10), s, eD))
      .reduce((sum, x) => sum + Number(x.total_cost || 0), 0);
    return {
      total: items.reduce((sum, x) => sum + Number(x.total || 0), 0),
      cogs: items.reduce((sum, x) => sum + Number(x.cost_of_sales || 0), 0) + internalCogs,
      cogs_sales: items.reduce((sum, x) => sum + Number(x.cost_of_sales || 0), 0),
      cogs_internal: internalCogs,
      count: items.length,
    };
  }, [invoices, internalIssues]);

  const submitVoid = async () => {
    if (!voidFor) return;
    setVoiding(true);
    try {
      await api.post(`/invoices/${voidFor.invoice_id}/void`, { reason: voidReason || "" });
      toast.success(lang === "ar" ? "تم إلغاء الفاتورة" : "Invoice voided");
      setVoidFor(null); setVoidReason(""); setViewInvoice(null); load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
    finally { setVoiding(false); }
  };

  const addExpense = async () => {
    if (!form.description || !parseFloat(form.amount)) { toast.error(t("required_field")); return; }
    try {
      await api.post("/expenses", {
        ...form, amount: parseFloat(form.amount),
        account_id: form.account_id || null,
        end_date: form.recurring ? (form.end_date || null) : null,
      });
      toast.success(t("saved"));
      setForm(emptyForm()); setOpen(false); load();
    } catch { toast.error(t("error_generic")); }
  };

  const removeExp = async (id) => {
    const ok = await confirm({
      title: lang === "ar" ? "حذف المصروف؟" : "Delete expense?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: t("cancel"),
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/expenses/${id}`); load();
  };

  const TABS = [
    { id: "overview", l_ar: "نظرة عامة", l_en: "Overview" },
    { id: "expenses", l_ar: "المصروفات", l_en: "Expenses" },
    { id: "invoices", l_ar: "الفواتير", l_en: "Invoices" },
    { id: "analytics", l_ar: "تحليلات", l_en: "Analytics" },
  ];

  return (
    <div className="space-y-5" data-testid="accounting-page">
      <div className="flex items-center justify-between flex-wrap gap-3 no-print">
        <h1 className="font-heading text-4xl gold-text">{t("nav_accounting")}</h1>
      </div>

      <div className="flex gap-2 flex-wrap no-print">
        {TABS.map(x => (
          <button key={x.id} onClick={() => setTab(x.id)} data-testid={`tab-${x.id}`}
            className={`px-5 py-2.5 rounded-md text-sm border ${tab === x.id ? "btn-gold" : "border-[var(--border)] text-[var(--text)] bg-[var(--surface)]"}`}>
            {lang === "ar" ? x.l_ar : x.l_en}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <OverviewTab summary={summary} cashPosition={cashPosition} monthlyExp={monthlyExp} monthlyInv={monthlyInv} lang={lang} onPrintPL={() => setPlPickerOpen(true)} plLoading={plLoading}/>
      )}
      {tab === "expenses" && (
        <ExpensesTab expenses={expenses} accounts={accounts} lang={lang} template={invoiceTemplate}
          onAdd={() => { setForm(emptyForm()); setOpen(true); }} onDelete={removeExp}/>
      )}
      {tab === "invoices" && (
        <InvoicesTab invoices={invoices} lang={lang} canVoid={canVoidInvoice} template={invoiceTemplate}
          onVoid={(i) => { setVoidFor(i); setVoidReason(""); }}
          onView={(i) => setViewInvoice(i)}/>
      )}
      {tab === "analytics" && (
        <AnalyticsTab expenses={expenses} invoices={invoices} lang={lang}/>
      )}

      {/* P&L period picker modal */}
      {plPickerOpen && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/40 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setPlPickerOpen(false))}>
          <div className="card-surface p-6 w-full max-w-lg space-y-4" onMouseDown={onModalPanelMouseDown} data-testid="pl-picker-modal">
            <div className="flex items-center gap-2">
              <Printer size={20} className="text-[var(--gold)]"/>
              <h2 className="font-heading text-2xl gold-text">
                {lang === "ar" ? "اختر الفترة الزمنية" : "Select Period"}
              </h2>
            </div>
            <p className="text-sm text-[var(--text-muted)]">
              {lang === "ar"
                ? "اختر الفترة التي تريد طباعة قائمة الأرباح والخسائر عنها — البيانات تُحسب لحظياً من الخادم."
                : "Choose the period for the P&L statement — figures are computed live from the server."}
            </p>

            {/* Preset chips */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {[
                { v: "month", l_ar: "هذا الشهر", l_en: "This month" },
                { v: "prev_month", l_ar: "الشهر السابق", l_en: "Prev month" },
                { v: "ytd", l_ar: "من بداية السنة", l_en: "Year to date" },
                { v: "year", l_ar: "السنة الحالية", l_en: "This year" },
                { v: "prev_year", l_ar: "السنة السابقة", l_en: "Prev year" },
                { v: "specific_month", l_ar: "شهر محدد", l_en: "Specific month" },
                { v: "custom", l_ar: "مخصص", l_en: "Custom" },
              ].map(p => (
                <button key={p.v} onClick={() => applyPlPreset(p.v)} data-testid={`pl-preset-${p.v}`}
                  className={`px-3 py-2 rounded-md text-xs border text-center transition ${plPreset === p.v ? "btn-gold" : "border-[var(--border)] bg-[var(--surface)] text-[var(--text)] hover:border-[var(--gold)]"}`}>
                  {lang === "ar" ? p.l_ar : p.l_en}
                </button>
              ))}
            </div>

            {plPreset === "specific_month" && (
              <div>
                <label className="text-xs text-[var(--text-muted)] block mb-1">
                  {lang === "ar" ? "اختر الشهر" : "Pick a month"}
                </label>
                <input type="month" value={plMonth} onChange={(e) => setPlMonth(e.target.value)}
                  className="input-dark" data-testid="pl-month-picker"/>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 pt-2 border-t border-[var(--border-subtle)]">
              <div>
                <label className="text-[10px] text-[var(--text-muted)] block">{lang === "ar" ? "من" : "From"}</label>
                <input type="date" value={plRange.start} onChange={(e) => { setPlRange({ ...plRange, start: e.target.value }); setPlPreset("custom"); }}
                  className="input-dark text-sm" data-testid="pl-from"/>
              </div>
              <div>
                <label className="text-[10px] text-[var(--text-muted)] block">{lang === "ar" ? "إلى" : "To"}</label>
                <input type="date" value={plRange.end} onChange={(e) => { setPlRange({ ...plRange, end: e.target.value }); setPlPreset("custom"); }}
                  className="input-dark text-sm" data-testid="pl-to"/>
              </div>
            </div>

            <div className="text-xs text-[var(--text-muted)] bg-[var(--gold-tint)] rounded-md p-3 border border-[var(--border-subtle)]">
              <div className="flex justify-between mb-1">
                <span>{lang === "ar" ? "الفترة المختارة" : "Selected period"}:</span>
                <span className="text-[var(--text)] font-medium">{periodLabel()}</span>
              </div>
              <div className="num-display text-[var(--text)] flex justify-between">
                <span>{plRange.start}</span>
                <span>→</span>
                <span>{plRange.end}</span>
              </div>
            </div>

            <div className="flex gap-3 justify-end pt-2">
              <button className="px-4 py-2 rounded-md border border-[var(--border)] text-sm" onClick={() => setPlPickerOpen(false)}>
                {t("cancel")}
              </button>
              <button onClick={handlePrintPL} disabled={plLoading} data-testid="pl-print-confirm"
                className="btn-gold px-5 py-2 rounded-md text-sm flex items-center gap-2 disabled:opacity-50">
                <Printer size={14}/>
                {plLoading ? (lang === "ar" ? "جارٍ التحميل..." : "Loading...") : (lang === "ar" ? "طباعة" : "Print")}
              </button>
            </div>
          </div>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-6 w-full max-w-2xl space-y-4 max-h-[92vh] overflow-auto" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-2xl gold-text">{t("add_expense")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("expense_description")}</label><input className="input-dark" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="expense-desc"/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("amount")}</label><input type="number" className="input-dark num-display" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="expense-amount"/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("category")}</label><input className="input-dark" value={form.category} onChange={e => setForm({...form, category: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("status")}</label>
                <select className="input-dark" value={form.expense_type} onChange={e => setForm({...form, expense_type: e.target.value})}>
                  <option value="variable">{t("variable_expense")}</option>
                  <option value="fixed">{t("fixed_expense")}</option>
                </select>
              </div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("from_account")}</label>
                <select className="input-dark" value={form.account_id} onChange={e => setForm({...form, account_id: e.target.value})}>
                  <option value="">---</option>
                  {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                </select>
              </div>
              <div className="md:col-span-2 flex items-center gap-2 pt-3">
                <input type="checkbox" checked={form.recurring} onChange={e => setForm({...form, recurring: e.target.checked})} data-testid="expense-recurring"/>
                <span className="text-sm text-[var(--text)]">{t("recurring")}</span>
              </div>
              {!form.recurring && (
                <div><label className="text-xs text-[var(--text-muted)]">{t("date")}</label><input type="date" className="input-dark" value={form.date} onChange={e => setForm({...form, date: e.target.value})}/></div>
              )}
              {form.recurring && <>
                <div><label className="text-xs text-[var(--text-muted)]">{t("start_date")}</label><input type="date" className="input-dark" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})}/></div>
                <div><label className="text-xs text-[var(--text-muted)]">{t("end_date")}</label><input type="date" className="input-dark" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})}/></div>
              </>}
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="expense-save" onClick={addExpense}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {viewInvoice && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setViewInvoice(null))}>
          <div className="card-surface max-w-lg w-full p-6 max-h-[90vh] overflow-auto" onMouseDown={onModalPanelMouseDown} data-testid="invoice-detail-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-heading text-xl gold-text">#{viewInvoice.invoice_number}</div>
                <div className="text-xs text-[var(--text-muted)] mt-1">{new Date(viewInvoice.created_at).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}</div>
              </div>
              <div className="flex items-center gap-2">
                {viewInvoice.status === "cancelled" && <span className="badge badge-red">{lang === "ar" ? "ملغاة" : "VOID"}</span>}
                <button onClick={() => setViewInvoice(null)} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18}/></button>
              </div>
            </div>
            <div className="space-y-1 text-sm mb-4">
              <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("customer")}</span><span className="font-medium">{viewInvoice.customer_name || "—"}</span></div>
              {viewInvoice.customer_phone && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{lang === "ar" ? "الهاتف" : "Phone"}</span><span dir="ltr">{viewInvoice.customer_phone}</span></div>}
              <div className="flex justify-between"><span className="text-[var(--text-muted)]">{lang === "ar" ? "الدفع" : "Payment"}</span><span>{formatPaymentLabel(viewInvoice, lang)}</span></div>
              {viewInvoice.cancelled_at && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{lang === "ar" ? "أُلغيت في" : "Voided at"}</span><span className="text-rose-400 num-display">{new Date(viewInvoice.cancelled_at).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}</span></div>}
              {viewInvoice.cancel_reason && <div className="flex justify-between gap-4"><span className="text-[var(--text-muted)] shrink-0">{lang === "ar" ? "سبب الإلغاء" : "Void reason"}</span><span className="text-rose-300 text-end">{viewInvoice.cancel_reason}</span></div>}
            </div>
            <div className="border-t border-[var(--border-subtle)] pt-3 mb-3">
              <div className="text-xs text-[var(--text-muted)] mb-2">{lang === "ar" ? "البنود" : "Items"}</div>
              {(viewInvoice.items || []).map((it, i) => {
                const itemName = lang === "ar" ? (it.name_ar || it.name) : (it.name_en || it.name);
                const unitPrice = it.unit_price ?? it.price ?? 0;
                const qty = it.qty || 1;
                const lineTotal = it.line_total != null ? Number(it.line_total) : Number(unitPrice) * qty;
                return (
                  <div key={i} className="flex justify-between text-sm py-1.5 border-b border-[var(--border-subtle)] last:border-0">
                    <div className="min-w-0 flex-1">
                      <div className="truncate" title={itemName}>{itemName}</div>
                      {qty > 1 && <div className="text-[10px] text-[var(--text-muted)]">{Number(unitPrice).toFixed(2)} × {qty}</div>}
                    </div>
                    <div className="ms-3 font-mono">{lineTotal.toFixed(2)}</div>
                  </div>
                );
              })}
            </div>
            <div className="space-y-1 text-sm border-t border-[var(--border-subtle)] pt-3">
              <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("subtotal")}</span><span>{Number(viewInvoice.subtotal || 0).toFixed(2)}</span></div>
              {viewInvoice.discount > 0 && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("discount")}</span><span>-{Number(viewInvoice.discount).toFixed(2)}</span></div>}
              {viewInvoice.coupon_discount > 0 && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{lang === "ar" ? "كوبون" : "Coupon"} ({viewInvoice.coupon_code})</span><span className="gold-text">-{Number(viewInvoice.coupon_discount).toFixed(2)}</span></div>}
              {viewInvoice.loyalty_value_applied > 0 && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("points_value")}</span><span>-{Number(viewInvoice.loyalty_value_applied).toFixed(2)}</span></div>}
              {viewInvoice.tax > 0 && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("tax")}</span><span>{Number(viewInvoice.tax).toFixed(2)}</span></div>}
              <div className="flex justify-between font-heading text-xl pt-2 border-t border-[var(--border-subtle)]"><span>{t("total")}</span><span className="gold-text">{Number(viewInvoice.total).toFixed(2)}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-5">
              <button onClick={() => printInvoiceNow(viewInvoice, "pos", invoiceTemplate, lang)} data-testid="invoice-detail-print-pos" className="btn-gold py-2.5 rounded-md text-sm flex items-center justify-center gap-2"><Receipt size={14}/>{lang === "ar" ? "طباعة POS" : "POS Print"}</button>
              <button onClick={() => printInvoiceNow(viewInvoice, "a4", invoiceTemplate, lang)} data-testid="invoice-detail-print-a4" className="btn-rose-outline py-2.5 rounded-md text-sm flex items-center justify-center gap-2"><FileText size={14}/>{lang === "ar" ? "فاتورة A4" : "A4 Invoice"}</button>
            </div>
            {canVoidInvoice && viewInvoice.status !== "cancelled" && (
              <button onClick={() => { setVoidFor(viewInvoice); setVoidReason(""); }} data-testid="invoice-detail-void-btn"
                className="w-full mt-3 px-4 py-2.5 rounded-md border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 text-sm font-medium flex items-center justify-center gap-2">
                <XCircle size={14}/>{lang === "ar" ? "إلغاء الفاتورة" : "Void Invoice"}
              </button>
            )}
          </div>
        </div>
      )}

      {voidFor && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setVoidFor(null))}>
          <div className="card-surface p-6 w-full max-w-md" onMouseDown={onModalPanelMouseDown} data-testid="void-invoice-modal">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-rose-500/15 flex items-center justify-center shrink-0">
                <AlertTriangle className="text-rose-400" size={20}/>
              </div>
              <div>
                <h3 className="font-heading text-lg text-[var(--text)]">{lang === "ar" ? "إلغاء الفاتورة" : "Void Invoice"}</h3>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  {lang === "ar"
                    ? `سيتم إلغاء الفاتورة #${voidFor.invoice_number} (${fmt(voidFor.total)} ر.ق) وإعادة المنتجات للمخزون.`
                    : `Invoice #${voidFor.invoice_number} (${fmt(voidFor.total)} QAR) will be voided.`}
                </p>
              </div>
            </div>
            <div>
              <label className="text-xs text-[var(--text-muted)] block mb-1">{lang === "ar" ? "سبب الإلغاء (اختياري)" : "Reason (optional)"}</label>
              <textarea rows={3} value={voidReason} onChange={e => setVoidReason(e.target.value)} data-testid="void-reason-input" className="input-dark w-full"/>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setVoidFor(null)} className="flex-1 px-4 py-2 rounded-md border border-[var(--border)] text-sm">{t("cancel")}</button>
              <button onClick={submitVoid} disabled={voiding} data-testid="confirm-void-btn"
                className="flex-1 px-4 py-2 rounded-md bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-50">
                {voiding ? (lang === "ar" ? "جارٍ..." : "Voiding...") : (lang === "ar" ? "تأكيد الإلغاء" : "Confirm Void")}
              </button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
